import React, { useState, useEffect } from 'react';
import { Check } from 'lucide-react';

interface DynamicTableProps {
  data: any[];
  excludeColumns?: string[];
  onRowSelect?: (selectedRows: any[]) => void;
}

const CustomCheckbox = ({
  checked,
  onCheckedChange,
  ariaLabel
}: {
  checked: boolean,
  onCheckedChange: (checked: boolean) => void,
  ariaLabel?: string
}) => {
  return (
    <div
      role="checkbox"
      aria-checked={checked}
      aria-label={ariaLabel}
      tabIndex={0}
      className={`h-5 w-5 shrink-0 rounded-sm border cursor-pointer transition-colors duration-200 ${
        checked
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-primary bg-background'
      }`}
      onClick={() => onCheckedChange(!checked)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onCheckedChange(!checked);
        }
      }}
    >
      {checked && (
        <Check className="h-[18px] w-[18px] text-white" />
      )}
    </div>
  );
};

const DynamicTable = ({ data, excludeColumns = [], onRowSelect }: DynamicTableProps) => {
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [selectedRows, setSelectedRows] = useState<boolean[]>([]);

  useEffect(() => {
    // Initialize selectedRows state based on the length of sortedData
    setSelectedRows(Array(data.length).fill(false));
  }, [data.length]);

  if (!data || data.length === 0) {
    return (
      <div className='p-4'>
        <div className="flex items-center justify-center h-64 border rounded-md">
        <p className="text-gray-500">No data available</p>
      </div>
      </div>
    );
  }

  // Get column headers from the first item
  const firstItem = data[0];
  const columns = Object.keys(firstItem).filter(key => !excludeColumns.includes(key));

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedData = [...data].sort((a, b) => {
    if (!sortField) return 0;

    const aValue = a[sortField];
    const bValue = b[sortField];

    if (aValue === undefined || bValue === undefined) return 0;
    if (aValue === bValue) return 0;

    const compareResult = typeof aValue === 'string'
      ? aValue.localeCompare(bValue)
      : aValue < bValue ? -1 : 1;

    return sortDirection === 'asc' ? compareResult : -compareResult;
  });

  // Format cell value based on column type
  const formatCellValue = (row: any, column: string) => {
    const value = row[column];

    // Skip rendering for null/undefined values
    if (value === null || value === undefined) return '-';

    // Special formatting for status fields (like recall, pending, etc.)
    if (column === 'status') {
      return (
        <span className={`px-2 py-1 rounded-full text-xs ${
          value === 'Recall'
            ? 'bg-red-100 text-red-800'
            : value === 'Pending'
            ? 'bg-yellow-100 text-yellow-800'
            : value === 'Booked'
            ? 'bg-green-100 text-green-800'
            : value === 'Upcoming'
            ? 'bg-green-100 text-green-800'
            : value === 'In Progress'
            ? 'bg-yellow-100 text-yellow-800'
            : 'bg-blue-100 text-blue-800'
        }`}>
          {value}
        </span>
      );
    }

    // Special formatting for phone numbers
    if (column === 'primaryPhone' || column === 'customerPhone') {
      return <span className="font-medium text-blue-600">{value}</span>;
    }

    return value;
  };

  const handleRowSelection = (index: number) => {
    const newSelectedRows = [...selectedRows];
    newSelectedRows[index] = !newSelectedRows[index];
    setSelectedRows(newSelectedRows);

    if (onRowSelect) {
      const selectedItems = sortedData.filter((_, i) => newSelectedRows[i]);
      onRowSelect(selectedItems);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    const newSelectedRows = Array(sortedData.length).fill(checked);
    setSelectedRows(newSelectedRows);

    if (onRowSelect) {
      const selectedItems = checked ? [...sortedData] : [];
      onRowSelect(selectedItems);
    }
  };

  const isAllSelected = selectedRows.length > 0 && selectedRows.every(Boolean);

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-gray-50">
            {onRowSelect && (
              <th className="py-3 px-4 text-left">
                <CustomCheckbox
                  checked={isAllSelected}
                  onCheckedChange={handleSelectAll}
                  ariaLabel="Select all rows"
                />
              </th>
            )}
            {columns.map(column => (
              <th
                key={column}
                className="text-left py-3 px-4 font-medium text-sm text-gray-600 cursor-pointer hover:bg-gray-100 transition-colors duration-200"
                onClick={() => handleSort(column)}
              >
                <div className="flex items-center">
                  {column.charAt(0).toUpperCase() + column.slice(1).replace(/([A-Z])/g, ' $1')}
                  {sortField === column && (
                    <span className="ml-1">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </div>
              </th>
            ))}
            {/* <th className="text-right py-3 px-4 font-medium text-sm text-gray-600">
              Actions
            </th> */}
          </tr>
        </thead>
        <tbody>
          {sortedData.map((row, rowIndex) => (
            <tr
              key={rowIndex}
              className={`border-t hover:bg-gray-50 transition-colors duration-200 ${
                selectedRows[rowIndex] ? 'bg-blue-50' : ''
              }`}
            >
              {onRowSelect && (
                <td className="py-3 px-4">
                  <CustomCheckbox
                    checked={selectedRows[rowIndex] || false}
                    onCheckedChange={() => handleRowSelection(rowIndex)}
                    ariaLabel={`Select row ${rowIndex + 1}`}
                  />
                </td>
              )}
              {columns.map(column => (
                <td key={column} className="py-3 px-4">
                  {formatCellValue(row, column)}
                </td>
              ))}
              {/* <td className="py-3 px-4 text-right">
                <button
                  className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                >
                  View
                </button>
              </td> */}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default DynamicTable;
